import { useEffect, useState } from "react";
import type { LayoutData } from "@/types/domain";
import { fetchLayoutData } from "@/api/layout/api";

// Scope the cache to the server-rendered user so a different user on the same tab gets a miss, not stale data.
const currentUserId = (): string =>
  (document.querySelector('input[name="UserId"]') as HTMLInputElement | null)?.value ?? "";

const cacheKey = () => `vecta_layout_data:${currentUserId()}`;

const readCache = (): LayoutData | null => {
  try {
    const raw = sessionStorage.getItem(cacheKey());
    return raw ? (JSON.parse(raw) as LayoutData) : null;
  } catch {
    return null;
  }
};

const writeCache = (data: LayoutData) => {
  try { sessionStorage.setItem(cacheKey(), JSON.stringify(data)); } catch { /* non-critical */ }
};

type UseLayoutDataResult = {
  data: LayoutData | null;
  loading: boolean;
  error: string | null;
}

export const useLayoutData = (): UseLayoutDataResult => {
  const cached = readCache();
  const [data, setData] = useState<LayoutData | null>(cached);
  const [loading, setLoading] = useState(cached === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchLayoutData()
      .then((json) => {
        if (!cancelled) {
          writeCache(json);
          // Only re-render if something actually changed — avoids cascading context updates on warm sessions
          setData((prev) => (JSON.stringify(prev) === JSON.stringify(json) ? prev : json));
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && !data) {
          setError(err instanceof Error ? err.message : "Failed to load layout data");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
